/**
 * Calculation settings store.
 *
 * These constants (bitrates, storage per hour, efficiency factors) calibrate
 * every quote the company issues, so the database is the source of truth rather
 * than each person's browser. Resolution order:
 *
 *   1. the API — the team's shared, versioned values
 *   2. localStorage — a mirror, so a reload still works offline or with no API
 *   3. the defaults compiled into appConfig
 *
 * `values` is a flat { category: { key: number } } map — only the numbers a user
 * can change. `effective` re-inflates those onto the shape appConfig uses, so
 * the pricing engine consumes one object either way.
 */
import APP_CONFIG from '../config/appConfig';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const MIRROR_KEY = 'inorain.calculationSettings';

/** The editable numbers, straight from the compiled defaults. */
export const defaultSettingValues = () => {
    const out = {};
    Object.entries(APP_CONFIG.calculationSettings).forEach(([category, group]) => {
        out[category] = {};
        Object.entries(group).forEach(([key, setting]) => {
            if (setting && typeof setting === 'object' && setting.value !== undefined) {
                out[category][key] = setting.value;
            } else if (typeof setting === 'number') {
                out[category][key] = setting;
            }
        });
    });
    return out;
};

/**
 * Re-inflate flat values onto the appConfig shape, clamped to each setting's
 * declared min/max so a bad stored value cannot produce absurd capacity.
 */
export const resolveCalculationSettings = (values) => {
    const effective = {};
    Object.entries(APP_CONFIG.calculationSettings).forEach(([category, group]) => {
        effective[category] = {};
        Object.entries(group).forEach(([key, setting]) => {
            if (!setting || typeof setting !== 'object' || setting.value === undefined) {
                effective[category][key] = setting;
                return;
            }
            const override = values?.[category]?.[key];
            let value = typeof override === 'number' && Number.isFinite(override)
                ? override
                : setting.value;
            if (typeof setting.min === 'number') value = Math.max(setting.min, value);
            if (typeof setting.max === 'number') value = Math.min(setting.max, value);
            effective[category][key] = { ...setting, value };
        });
    });
    return effective;
};

const readMirror = () => {
    try {
        const raw = localStorage.getItem(MIRROR_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writeMirror = (values) => {
    try {
        localStorage.setItem(MIRROR_KEY, JSON.stringify(values));
    } catch (e) {
        // A blocked store is not fatal; the API remains the source of truth.
    }
};

/** Deep-merge stored values over the defaults, so a partial row still works. */
const mergeOverDefaults = (stored) => {
    const base = defaultSettingValues();
    if (!stored) return base;
    Object.keys(base).forEach(category => {
        Object.keys(base[category]).forEach(key => {
            const v = stored?.[category]?.[key];
            if (typeof v === 'number' && Number.isFinite(v)) base[category][key] = v;
        });
    });
    return base;
};

/**
 * Load the team's settings.
 * Never throws — an unreachable API degrades to the mirror, then to defaults.
 */
export const loadSettings = async () => {
    try {
        const response = await fetch(`${API_BASE}/settings`);
        if (!response.ok) throw new Error(`API responded ${response.status}`);
        const data = await response.json();
        if (data.settings) {
            const values = mergeOverDefaults(data.settings);
            writeMirror(values);
            return {
                values,
                source: 'database',
                version: data.version,
                updatedBy: data.updated_by,
                updatedAt: data.created_at
            };
        }
        // API is up but nothing has been saved for the team yet. Unsaved local
        // edits still win over the compiled defaults here — discarding them
        // would silently throw away work the moment the API answered.
        const pending = readMirror();
        if (pending) {
            return { values: mergeOverDefaults(pending), source: 'local', version: 0 };
        }
        return { values: defaultSettingValues(), source: 'defaults', version: 0 };
    } catch (error) {
        const mirrored = readMirror();
        if (mirrored) {
            return {
                values: mergeOverDefaults(mirrored),
                source: 'local',
                version: null,
                reason: error.message
            };
        }
        return {
            values: defaultSettingValues(),
            source: 'defaults',
            version: null,
            reason: error.message
        };
    }
};

/** Persist settings as a new version. Mirrors locally even if the API fails. */
export const saveSettings = async (values, meta = {}) => {
    writeMirror(values);
    try {
        const response = await fetch(`${API_BASE}/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ settings: values, updatedBy: meta.updatedBy, note: meta.note })
        });
        if (!response.ok) throw new Error(`API responded ${response.status}`);
        const data = await response.json();
        return { ok: true, persisted: true, version: data.version, updatedAt: data.created_at };
    } catch (error) {
        return { ok: true, persisted: false, reason: error.message };
    }
};

export const loadSettingsHistory = async (limit = 20) => {
    const response = await fetch(`${API_BASE}/settings/history?limit=${limit}`);
    if (!response.ok) throw new Error(`Could not load history (${response.status}).`);
    const data = await response.json();
    return data.history || [];
};
